"use client";

import { useState } from "react";
import { jwtDecode } from "jwt-decode";
import PizZip from "pizzip";
import { PDFDocument } from "pdf-lib";
import Docxtemplater from "docxtemplater";
import ImageModule from "docxtemplater-image-module-free";
import axios from "axios";
import { saveAs } from "file-saver";
import { NumericFormat } from "react-number-format";
import Select from "react-select";
import AlertDialog from "../components/AlertDialog";

import { useSovData } from "./SovUseSovData";
import {
  formatDollarValue,
  formatDateForDisplay,
  formatLocalDateNice,
  statusPillClass,
  customSelectStyles,
  minPercentForItem,
  isCreditMemoItem,
  invoiceComponentLabel,
  getBreakdownAllocation,
  getAddressString,
  toEmailArray,
} from "./SovHelpers";

// ═══════════════════════════════════════════════════════════════════════
// SOVTab3 — Schedule of Values for version-3 projects
//
// This file is intentionally just the shell: all state/data/business logic
// lives in useSovData.js, all pure calculations live in sovHelpers.js.
// What's left here is rendering, split into small local functions per
// section so no single block runs more than ~150 lines.
// ═══════════════════════════════════════════════════════════════════════

export default function SOVTab3({ project, setProject }) {
  const d = useSovData(project, setProject);

  if (d.isLoading) {
    return (
      <div className="flex items-center justify-center gap-3 py-12 text-gray-300">
        <span className="h-5 w-5 animate-spin rounded-full border-2 border-gray-500 border-t-orange-400"></span>
        Loading schedule of values…
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {!d.showCreateForm ? (
        <ListView d={d} project={project} />
      ) : (
        <DrawForm d={d} project={project} />
      )}

      <EmailModal d={d} project={project} />

      <AlertDialog
        type={d.alert.type}
        title={d.alert.title}
        message={d.alert.message}
        confirmText="OK"
        cancelText="Close"
        open={d.alert.open}
        onClose={d.closeAlert}
        onConfirm={d.closeAlert}
      />

      <AlertDialog
        type="warning"
        title={
          d.confirmUnlockIntent === "disable"
            ? "Turn Off Custom Breakdown?"
            : "Unlock Breakdown for Editing?"
        }
        message={
          d.confirmUnlockIntent === "disable"
            ? 'This breakdown already has billed work in previous draws. Turning it off will collapse everything back into a single "Original Contract Value" line. Your previously billed amounts are preserved, but the breakdown itself won\'t be recoverable. Continue?'
            : "This breakdown already has billed work in previous draws. You can still fix a title or amount, but changing an item that's already been billed does not change what was invoiced previously — it only affects how this item appears going forward. Continue?"
        }
        confirmText={
          d.confirmUnlockIntent === "disable" ? "Turn Off" : "Unlock"
        }
        cancelText="Cancel"
        open={d.confirmUnlockIntent !== null}
        onClose={() => d.setConfirmUnlockIntent(null)}
        onConfirm={() => {
          if (d.confirmUnlockIntent === "disable") d.disableCustomBreakdown();
          else {
            d.setBreakdownUnlocked(true);
            d.setIsBreakdownExpanded(true);
          }
          d.setConfirmUnlockIntent(null);
        }}
      />

      {d.showAddCoPicker && <AddCoPickerModal d={d} />}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// LIST VIEW — draws table, separate-CO invoices table, available items,
// invoice summary. Shown when d.showCreateForm is false.
// ─────────────────────────────────────────────────────────────────────────
function ListView({ d, project }) {
  const availableItems = [
    {
      description: d.isCustomBreakdown
        ? "Custom Breakdown"
        : "Original Contract Value",
      totalPrice: project.originalContractValue,
    },
    ...d.approvedChangeOrders.map((co) => ({
      description: co.title,
      reference: co.referenceNumber || null,
      coNumber: co.changeOrderNumber,
      totalPrice: co.totalCustomerPrice || co.totalPrice,
    })),
  ];

  return (
    <>
      <DrawsTable d={d} project={project} />
      {d.separateChangeOrders.length > 0 && <SeparateCoTable d={d} />}
      <AvailableItemsCard items={availableItems} />
      <InvoiceSummaryCard d={d} />
      {d.separateChangeOrders.length > 0 && (
        <SeparateInvoiceSummaryCard d={d} />
      )}
    </>
  );
}

function DrawsTable({ d, project }) {
  return (
    <div className="overflow-hidden rounded-lg shadow-xl bg-gray-700">
      <div className="flex flex-wrap items-center justify-between gap-3 px-6 py-4 border-b border-gray-600">
        <div>
          <h3 className="text-lg font-semibold text-gray-200">
            Progress Draws
          </h3>
          <p className="text-sm text-gray-400">
            {d.companyProfile?.companyName || "Company"} · {d.sovs.length} draw
            {d.sovs.length === 1 ? "" : "s"} on this project
          </p>
        </div>
        <div className="flex space-x-3">
          <button
            onClick={d.handleCreateSOV}
            className="bg-orange-400 text-white px-6 py-3 rounded-lg hover:bg-orange-500 transition font-medium shadow"
          >
            + Create Draw
          </button>
          {d.calculateOutstandingHoldback() > 0 && (
            <button
              onClick={d.handleInvoiceHoldbackSOV}
              className="bg-purple-600 text-white px-6 py-3 rounded-lg hover:bg-purple-500 transition font-medium shadow flex items-center gap-3"
            >
              <span>Invoice Holdback</span>
              <span className="text-sm bg-purple-800 px-3 py-1.5 rounded-full">
                {formatDollarValue(d.calculateOutstandingHoldback())}
              </span>
            </button>
          )}
        </div>
      </div>

      {d.sovs.length === 0 ? (
        <div className="px-6 py-10 text-center">
          <p className="text-gray-300 font-medium">No progress draws yet</p>
          <p className="text-sm text-gray-400 mt-1">
            Create the first draw to start invoicing this project. The invoice
            date will default to today with a 28-day due date.
          </p>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-600 bg-gray-700">
            <thead className="bg-gray-800">
              <tr>
                {[
                  "Draw #",
                  "Invoice Date",
                  "Due Date",
                  "Bill To",
                  "Draw Amount",
                  "Status",
                ].map((h, i) => (
                  <th
                    key={h}
                    className={`px-4 py-2 text-xs font-medium text-gray-400 uppercase ${i === 4 ? "text-right" : "text-left"}`}
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-600">
              {d.sovs.map((sov) => (
                <tr
                  key={sov._id}
                  onClick={() => d.handleViewSOV(sov)}
                  className="hover:bg-gray-500 cursor-pointer transition-colors duration-200"
                >
                  <td className="px-4 py-3 text-sm font-semibold text-orange-400">
                    #{sov.drawNumber || "—"}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-200">
                    {formatLocalDateNice(sov.invoiceDate)}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-200">
                    {formatLocalDateNice(sov.dueDate)}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-200">
                    {sov.billTo}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-200 text-right">
                    {formatDollarValue(sov.totalInvoiced)}
                  </td>
                  <td className="px-4 py-3 text-sm">
                    <span
                      className={`px-2.5 py-1 rounded-full text-xs font-medium capitalize ${statusPillClass(sov.status)}`}
                    >
                      {sov.status || "—"}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function SeparateCoTable({ d }) {
  return (
    <div className="overflow-hidden rounded-lg shadow-xl bg-gray-700 border border-purple-700/30">
      <div className="px-6 py-4 border-b border-gray-600 bg-purple-900/10">
        <h3 className="text-lg font-semibold text-purple-200">
          Separate Change Order Invoices
        </h3>
        <p className="text-sm text-gray-400">
          Approved change orders billed on their own invoice — kept fully apart
          from the project's progress draws and their holdback. Voided invoices
          are excluded from the figures below.
        </p>
      </div>
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-600 bg-gray-700">
          <thead className="bg-gray-800">
            <tr>
              {[
                "CO #",
                "Title",
                "Amount",
                "Invoice #",
                "Invoice Status",
                "Billed",
                "Tax",
                "Total",
                "Holdback",
                "Action",
              ].map((h, i) => (
                <th
                  key={h}
                  className={`px-4 py-2 text-xs font-medium text-gray-400 uppercase whitespace-nowrap ${
                    i === 2 || i === 5 || i === 6 || i === 7
                      ? "text-right"
                      : "text-left"
                  }`}
                >
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-600">
            {d.separateChangeOrders.map((co) => {
              const sov = d.standaloneSovsByCoId[co._id];
              const amount = parseFloat(co.totalCustomerPrice || 0);
              const num = (v) => parseFloat(v) || 0;

              // Voided documents are history only — never count them.
              const allComponents = sov?.invoiceComponents || [];
              const components = allComponents.filter(
                (ic) => ic.status !== "voided",
              );
              const allVoided =
                allComponents.length > 0 && components.length === 0;

              const billed = components
                .filter((ic) => ic.component !== "holdback")
                .reduce((s, ic) => s + num(ic.amount), 0);
              const tax = components.reduce((s, ic) => s + num(ic.tax), 0);
              const total = components.reduce((s, ic) => s + num(ic.total), 0);
              const qbInvoiceNumber = components.find(
                (ic) => ic.qbInvoiceNumber,
              )?.qbInvoiceNumber;

              // Status reflects live invoices only.
              const statusLabel = !sov
                ? "Not started"
                : allVoided
                  ? "voided"
                  : components.length === 0
                    ? sov.status || "new"
                    : components.every((ic) => ic.status === "paid")
                      ? "paid"
                      : components.some((ic) => ic.status === "sent")
                        ? "sent"
                        : sov.status;

              const hasLive = components.length > 0;

              return (
                <tr
                  key={co._id}
                  className="hover:bg-gray-600 transition-colors duration-200"
                >
                  <td className="px-4 py-3 text-sm font-semibold text-purple-300">
                    #{co.changeOrderNumber}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-200">
                    {co.title}
                    {co.referenceNumber && (
                      <span className="ml-2 text-xs text-gray-500">
                        Ref: {co.referenceNumber}
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-200 text-right">
                    {formatDollarValue(amount)}
                  </td>
                  <td className="px-4 py-3 text-sm font-medium text-orange-400">
                    {qbInvoiceNumber || "—"}
                  </td>
                  <td className="px-4 py-3 text-sm">
                    {!sov ? (
                      <span className="px-2.5 py-1 rounded-full text-xs font-medium bg-gray-600/40 text-gray-300 border border-gray-500/40">
                        Not started
                      </span>
                    ) : (
                      <span
                        className={`px-2.5 py-1 rounded-full text-xs font-medium capitalize ${statusPillClass(statusLabel)}`}
                      >
                        {statusLabel}
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-200 text-right">
                    {hasLive ? formatDollarValue(billed) : "—"}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-300 text-right">
                    {hasLive ? formatDollarValue(tax) : "—"}
                  </td>
                  <td className="px-4 py-3 text-sm font-semibold text-white text-right">
                    {hasLive ? formatDollarValue(total) : "—"}
                  </td>
                  <td className="px-4 py-3 text-sm">
                    {sov?.withHoldback && hasLive ? (
                      <span className="text-purple-300">
                        10% —{" "}
                        {formatDollarValue(sov.holdbackInvoiceAmount || 0)}{" "}
                        prepared
                      </span>
                    ) : (
                      <span className="text-gray-500">—</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <button
                      onClick={() => d.handleOpenStandaloneSov(co)}
                      disabled={d.loadingStandaloneId === co._id}
                      className="px-3 py-1.5 text-xs font-medium text-white bg-purple-600 rounded-md hover:bg-purple-500 disabled:opacity-50 transition-colors"
                    >
                      {d.loadingStandaloneId === co._id
                        ? "…"
                        : !sov
                          ? "+ Create Invoice"
                          : allVoided
                            ? "Re-invoice"
                            : "Open Invoice"}
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function AvailableItemsCard({ items }) {
  return (
    <div className="overflow-hidden rounded-lg shadow-xl bg-gray-700">
      <div className="px-6 py-4 border-b border-gray-600">
        <h3 className="text-lg font-semibold text-gray-200">
          Available Contract Items
        </h3>
        <p className="text-sm text-gray-400">
          Everything below is pulled into new draws automatically — approved &
          billable change orders included.
        </p>
      </div>
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-600 bg-gray-700">
          <thead className="bg-gray-800">
            <tr>
              <th className="px-4 py-2 text-left text-xs font-medium text-gray-400 uppercase">
                Description
              </th>
              <th className="px-4 py-2 text-left text-xs font-medium text-gray-400 uppercase">
                Reference
              </th>
              <th className="px-4 py-2 text-right text-xs font-medium text-gray-400 uppercase">
                Total Price
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-600">
            {items.map((item, index) => (
              <tr
                key={index}
                className="hover:bg-gray-600 transition-colors duration-200"
              >
                <td className="px-4 py-2 text-sm text-gray-200">
                  {item.coNumber && (
                    <span className="mr-2 inline-flex items-center rounded bg-orange-500/10 border border-orange-500/30 px-2 py-0.5 text-xs font-medium text-orange-300">
                      CO #{item.coNumber}
                    </span>
                  )}
                  {item.description}
                </td>
                <td className="px-4 py-2 text-sm">
                  {item.reference ? (
                    <span className="inline-flex items-center rounded bg-gray-800 px-2 py-0.5 text-xs text-gray-300">
                      Ref: {item.reference}
                    </span>
                  ) : (
                    <span className="text-gray-500 text-xs">—</span>
                  )}
                </td>
                <td className="px-4 py-2 text-sm text-gray-200 text-right">
                  {formatDollarValue(item.totalPrice)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function SeparateInvoiceSummaryCard({ d }) {
  const num = (v) => parseFloat(v) || 0;

  // One row per invoice component; a CO with no invoice yet still gets a
  // placeholder row so unbilled separate work stays visible.
  const rows = d.separateChangeOrders
    .flatMap((co) => {
      const sov = d.standaloneSovsByCoId[co._id];
      const comps = sov?.invoiceComponents || [];
      if (comps.length === 0) return [{ co, sov, comp: null }];
      return comps.map((comp) => ({ co, sov, comp }));
    })
    .sort((a, b) => {
      const coDiff =
        (parseInt(a.co.changeOrderNumber) || 0) -
        (parseInt(b.co.changeOrderNumber) || 0);
      if (coDiff !== 0) return coDiff;
      const aHb = a.comp?.component === "holdback" ? 1 : 0;
      const bHb = b.comp?.component === "holdback" ? 1 : 0;
      return aHb - bHb;
    });

  // Voided invoices are shown in the table but excluded from all totals.
  const live = rows.filter((r) => r.comp && r.comp.status !== "voided");
  const sumLive = (field) => live.reduce((s, r) => s + num(r.comp[field]), 0);

  const coContractValue = d.separateChangeOrders.reduce(
    (s, co) => s + num(co.totalCustomerPrice || co.totalPrice),
    0,
  );
  const workBilled = live
    .filter((r) => r.comp.component !== "holdback")
    .reduce((s, r) => s + num(r.comp.amount), 0);
  const unbilled = Math.max(0, coContractValue - workBilled);

  const holdbackDeducted = live
    .filter((r) => r.comp.component !== "holdback")
    .reduce((s, r) => s + num(r.comp.holdback), 0);
  const holdbackReleased = live
    .filter((r) => r.comp.component === "holdback")
    .reduce((s, r) => s + num(r.comp.amount), 0);
  const holdbackRetained = Math.max(0, holdbackDeducted - holdbackReleased);

  const sepTotal = sumLive("total");
  const sepPaid = sumLive("amountPaid");
  const sepOutstanding = sepTotal - sepPaid;

  // Combined with progress draws
  const progressComps = d.sovs
    .flatMap((sov) => sov.invoiceComponents || [])
    .filter((ic) => ic.status !== "voided");
  const progTotal = progressComps.reduce((s, ic) => s + num(ic.total), 0);
  const progPaid = progressComps.reduce((s, ic) => s + num(ic.amountPaid), 0);
  const combinedHoldback = d.calculateOutstandingHoldback() + holdbackRetained;

  return (
    <div className="overflow-hidden rounded-lg shadow-xl bg-gray-700 border border-purple-700/30">
      <div className="px-6 py-4 border-b border-gray-600 bg-purple-900/10">
        <h3 className="text-lg font-semibold text-purple-200">
          Separate CO Invoice Summary
        </h3>
        <p className="text-sm text-gray-400">
          Every invoice issued for separately-billed change orders. Voided
          invoices are listed but excluded from totals.
        </p>
      </div>

      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-600 bg-gray-700">
          <thead className="bg-gray-800">
            <tr>
              {[
                "CO #",
                "Title",
                "Component",
                "QB Doc #",
                "Invoice Date",
                "Due Date",
                "Amount",
                "Holdback",
                "Tax",
                "Total",
                "Paid",
                "Status",
              ].map((h, i) => (
                <th
                  key={h}
                  className={`px-4 py-2 text-xs font-medium text-gray-400 uppercase whitespace-nowrap ${i >= 6 && i <= 10 ? "text-right" : "text-left"}`}
                >
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-600">
            {rows.map(({ co, comp }, idx) => {
              const isVoid = comp?.status === "voided";
              return (
                <tr
                  key={`${co._id}-${idx}`}
                  className={`hover:bg-gray-600 transition-colors duration-200 ${isVoid ? "opacity-50" : ""}`}
                >
                  <td className="px-4 py-2 text-sm font-semibold text-purple-300">
                    #{co.changeOrderNumber}
                  </td>
                  <td className="px-4 py-2 text-sm text-gray-200">
                    {co.title}
                    {co.referenceNumber && (
                      <span className="ml-2 text-xs text-gray-500">
                        Ref: {co.referenceNumber}
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-2 text-sm text-gray-200 whitespace-nowrap">
                    {!comp
                      ? "—"
                      : comp.component === "holdback"
                        ? "Holdback Release"
                        : comp.qbDocType === "credit_memo"
                          ? "Credit Memo"
                          : "CO Work"}
                  </td>
                  <td className="px-4 py-2 text-sm font-medium text-orange-400">
                    {comp?.qbInvoiceNumber || "—"}
                  </td>
                  <td className="px-4 py-2 text-sm text-gray-200">
                    {comp?.invoiceDate
                      ? new Date(comp.invoiceDate).toLocaleDateString()
                      : "—"}
                  </td>
                  <td className="px-4 py-2 text-sm text-gray-200">
                    {comp?.dueDate
                      ? new Date(comp.dueDate).toLocaleDateString()
                      : "—"}
                  </td>
                  <td className="px-4 py-2 text-sm text-gray-200 text-right">
                    {comp ? formatDollarValue(comp.amount || 0) : "—"}
                  </td>
                  <td className="px-4 py-2 text-sm text-gray-200 text-right">
                    {comp ? formatDollarValue(comp.holdback || 0) : "—"}
                  </td>
                  <td className="px-4 py-2 text-sm text-gray-200 text-right">
                    {comp ? formatDollarValue(comp.tax || 0) : "—"}
                  </td>
                  <td className="px-4 py-2 text-sm text-gray-200 text-right font-medium">
                    {comp ? formatDollarValue(comp.total || 0) : "—"}
                  </td>
                  <td className="px-4 py-2 text-sm text-gray-200 text-right">
                    {comp ? formatDollarValue(comp.amountPaid || 0) : "—"}
                  </td>
                  <td className="px-4 py-2 text-sm">
                    {!comp ? (
                      <span className="px-2.5 py-1 rounded-full text-xs font-medium bg-gray-600/40 text-gray-300 border border-gray-500/40">
                        Not invoiced
                      </span>
                    ) : (
                      <span
                        className={`px-2.5 py-1 rounded-full text-xs font-medium capitalize ${statusPillClass(comp.status)}`}
                      >
                        {comp.status || "—"}
                      </span>
                    )}
                  </td>
                </tr>
              );
            })}
            <tr className="bg-gray-800 font-semibold">
              <td className="px-4 py-3 text-sm text-gray-200" colSpan="6">
                Totals (excl. voided)
              </td>
              <td className="px-4 py-3 text-sm text-gray-200 text-right">
                {formatDollarValue(sumLive("amount"))}
              </td>
              <td className="px-4 py-3 text-sm text-gray-200 text-right">
                {formatDollarValue(sumLive("holdback"))}
              </td>
              <td className="px-4 py-3 text-sm text-gray-200 text-right">
                {formatDollarValue(sumLive("tax"))}
              </td>
              <td className="px-4 py-3 text-sm text-right font-bold text-orange-400">
                {formatDollarValue(sepTotal)}
              </td>
              <td className="px-4 py-3 text-sm text-gray-200 text-right">
                {formatDollarValue(sepPaid)}
              </td>
              <td className="px-4 py-3"></td>
            </tr>
          </tbody>
        </table>
      </div>

      <div className="px-6 py-4 bg-gray-800 border-t border-gray-600">
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          {[
            {
              label: "Separate CO Value",
              value: coContractValue,
              color: "text-gray-200",
              sub: `${formatDollarValue(unbilled)} unbilled`,
            },
            {
              label: "Total Billed",
              value: sepTotal,
              color: "text-gray-200",
            },
            {
              label: "Total Paid",
              value: sepPaid,
              color: "text-green-400",
            },
            {
              label: "Outstanding Balance",
              value: sepOutstanding,
              color: "text-orange-400",
            },
            {
              label: "Holdback Retained",
              value: holdbackRetained,
              color: "text-purple-400",
            },
          ].map(({ label, value, color, sub }) => (
            <div key={label} className="bg-gray-700 p-4 rounded-lg">
              <p className="text-xs text-gray-400 uppercase">{label}</p>
              <p className={`text-xl font-bold ${color}`}>
                {formatDollarValue(value)}
              </p>
              {sub && <p className="text-xs text-gray-500 mt-1">{sub}</p>}
            </div>
          ))}
        </div>
      </div>

      <div className="px-6 py-4 bg-gray-900/60 border-t border-gray-600">
        <p className="text-xs font-semibold text-gray-400 uppercase mb-3">
          Combined Project Totals (Progress Draws + Separate COs)
        </p>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {[
            {
              label: "Total Billed",
              value: progTotal + sepTotal,
              color: "text-gray-200",
            },
            {
              label: "Total Paid",
              value: progPaid + sepPaid,
              color: "text-green-400",
            },
            {
              label: "Outstanding Balance",
              value: progTotal - progPaid + sepOutstanding,
              color: "text-orange-400",
            },
            {
              label: "Holdback Retained",
              value: combinedHoldback,
              color: "text-purple-400",
            },
          ].map(({ label, value, color }) => (
            <div
              key={label}
              className="bg-gray-800 p-4 rounded-lg border border-gray-700"
            >
              <p className="text-xs text-gray-400 uppercase">{label}</p>
              <p className={`text-xl font-bold ${color}`}>
                {formatDollarValue(value)}
              </p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function InvoiceSummaryCard({ d }) {
  // Voided documents stay listed (greyed) but never count toward totals.
  // Credit memos carry negative totals, so sums are net of credits.
  const allInvoices = d.sovs
    .flatMap((sov) => sov.invoiceComponents || [])
    .filter((ic) => ic.status !== "voided");
  if (allInvoices.length === 0) {
    return (
      <div className="overflow-hidden rounded-lg shadow-xl bg-gray-700">
        <h3 className="text-lg font-semibold text-gray-200 px-6 py-4">
          Invoice Summary
        </h3>
        <p className="text-gray-400 px-6 py-4">
          No invoices generated yet. Generate QuickBooks invoices from inside a
          draw.
        </p>
      </div>
    );
  }

  const rows = d.sovs
    .flatMap((sov) =>
      (sov.invoiceComponents || []).map((comp) => ({
        ...comp,
        drawNumber: sov.drawNumber,
        sovId: sov._id,
      })),
    )
    .sort((a, b) => {
      if (a.drawNumber !== b.drawNumber)
        return (a.drawNumber || 0) - (b.drawNumber || 0);
      const order = { original_contract: 0, change_order: 1, holdback: 2 };
      return (order[a.component] || 0) - (order[b.component] || 0);
    });

  const sumField = (field) =>
    allInvoices.reduce((sum, inv) => sum + (parseFloat(inv[field]) || 0), 0);

  return (
    <div className="overflow-hidden rounded-lg shadow-xl bg-gray-700">
      <h3 className="text-lg font-semibold text-gray-200 px-6 py-4">
        Invoice Summary
      </h3>
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-600 bg-gray-700">
          <thead className="bg-gray-800">
            <tr>
              {[
                "Draw #",
                "Component",
                "QB Invoice #",
                "Invoice Date",
                "Due Date",
                "Amount",
                "Holdback",
                "Tax",
                "Total",
                "Paid",
                "Status",
              ].map((h, i) => (
                <th
                  key={h}
                  className={`px-4 py-2 text-xs font-medium text-gray-400 uppercase ${i >= 5 && i <= 9 ? "text-right" : "text-left"}`}
                >
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-600">
            {rows.map((invoice, idx) => (
              <tr
                key={`${invoice.sovId}-${idx}`}
                className={`hover:bg-gray-600 transition-colors duration-200 ${invoice.status === "voided" ? "opacity-50" : ""}`}
              >
                <td className="px-4 py-2 text-sm text-gray-200">
                  #{invoice.drawNumber || "—"}
                </td>
                <td className="px-4 py-2 text-sm text-gray-200">
                  {invoiceComponentLabel(invoice)}
                </td>
                <td className="px-4 py-2 text-sm font-medium text-orange-400">
                  {invoice.qbInvoiceNumber || "—"}
                </td>
                <td className="px-4 py-2 text-sm text-gray-200">
                  {invoice.invoiceDate
                    ? new Date(invoice.invoiceDate).toLocaleDateString()
                    : "—"}
                </td>
                <td className="px-4 py-2 text-sm text-gray-200">
                  {invoice.dueDate
                    ? new Date(invoice.dueDate).toLocaleDateString()
                    : "—"}
                </td>
                <td className="px-4 py-2 text-sm text-gray-200 text-right">
                  {formatDollarValue(invoice.amount || 0)}
                </td>
                <td className="px-4 py-2 text-sm text-gray-200 text-right">
                  {formatDollarValue(invoice.holdback || 0)}
                </td>
                <td className="px-4 py-2 text-sm text-gray-200 text-right">
                  {formatDollarValue(invoice.tax || 0)}
                </td>
                <td className="px-4 py-2 text-sm text-gray-200 text-right font-medium">
                  {formatDollarValue(invoice.total || 0)}
                </td>
                <td className="px-4 py-2 text-sm text-gray-200 text-right">
                  {formatDollarValue(invoice.amountPaid || 0)}
                </td>
                <td className="px-4 py-2 text-sm">
                  <span
                    className={`px-2.5 py-1 rounded-full text-xs font-medium capitalize ${statusPillClass(invoice.status)}`}
                  >
                    {invoice.status || "—"}
                  </span>
                </td>
              </tr>
            ))}
            <tr className="bg-gray-800 font-semibold">
              <td className="px-4 py-3 text-sm text-gray-200" colSpan="5">
                Totals
              </td>
              <td className="px-4 py-3 text-sm text-gray-200 text-right">
                {formatDollarValue(sumField("amount"))}
              </td>
              <td className="px-4 py-3 text-sm text-gray-200 text-right">
                {formatDollarValue(sumField("holdback"))}
              </td>
              <td className="px-4 py-3 text-sm text-gray-200 text-right">
                {formatDollarValue(sumField("tax"))}
              </td>
              <td className="px-4 py-3 text-sm text-gray-200 text-right font-bold text-orange-400">
                {formatDollarValue(sumField("total"))}
              </td>
              <td className="px-4 py-3 text-sm text-gray-200 text-right">
                {formatDollarValue(sumField("amountPaid"))}
              </td>
              <td className="px-4 py-3"></td>
            </tr>
          </tbody>
        </table>
      </div>
      <div className="px-6 py-4 bg-gray-800 border-t border-gray-600">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {[
            {
              label: "Total Billed",
              value: sumField("total"),
              color: "text-gray-200",
            },
            {
              label: "Total Paid",
              value: sumField("amountPaid"),
              color: "text-green-400",
            },
            {
              label: "Outstanding Balance",
              value: rows
                .filter((inv) => inv.status !== "voided")
                .reduce(
                  (s, inv) =>
                    s +
                    ((parseFloat(inv.total) || 0) -
                      (parseFloat(inv.amountPaid) || 0)),
                  0,
                ),
              color: "text-orange-400",
            },
            {
              label: "Holdback Retained",
              value: d.calculateOutstandingHoldback(),
              color: "text-purple-400",
            },
          ].map(({ label, value, color }) => (
            <div key={label} className="bg-gray-700 p-4 rounded-lg">
              <p className="text-xs text-gray-400 uppercase">{label}</p>
              <p className={`text-xl font-bold ${color}`}>
                {formatDollarValue(value)}
              </p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function AddCoPickerModal({ d }) {
  return (
    <div className="fixed inset-0 bg-gray-800 bg-opacity-75 flex items-center justify-center z-50">
      <div className="bg-gray-700 p-6 rounded-lg shadow-xl w-full max-w-lg border border-gray-600">
        <h2 className="text-xl font-semibold text-gray-200 mb-4">
          Add Approved Change Order
        </h2>
        {d.eligibleCOsToAdd.length === 0 ? (
          <p className="text-sm text-gray-400">
            No approved change orders available that aren't already on this
            draw.
          </p>
        ) : (
          <div className="space-y-2 max-h-80 overflow-y-auto">
            {d.eligibleCOsToAdd.map((co) => (
              <button
                key={co._id}
                type="button"
                onClick={() => d.addChangeOrderToDraw(co)}
                className="w-full text-left px-4 py-3 rounded-md bg-gray-800 hover:bg-gray-600 transition-colors"
              >
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-gray-200">
                    CO #{co.changeOrderNumber} — {co.title}
                  </span>
                  <span className="text-sm text-orange-300">
                    {formatDollarValue(co.totalCustomerPrice || co.totalPrice)}
                  </span>
                </div>
                {co.referenceNumber && (
                  <span className="text-xs text-gray-400">
                    Ref: {co.referenceNumber}
                  </span>
                )}
              </button>
            ))}
          </div>
        )}
        <div className="flex justify-end mt-4">
          <button
            type="button"
            onClick={() => d.setShowAddCoPicker(false)}
            className="px-4 py-2 text-sm font-medium text-gray-200 bg-gray-600 rounded-md hover:bg-gray-500"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// CREATE / EDIT FORM — shared by normal draws and standalone CO invoices.
// d.isStandaloneMode gates the sections that don't apply to a single-line
// standalone invoice (draw #, breakdown, add-CO picker).
// ─────────────────────────────────────────────────────────────────────────
function DrawForm({ d, project }) {
  const isVoided = d.sovData.status === "voided";
  const isBusy = d.isSubmitting || d.isGeneratingInvoice || d.isDownloading;
  const breakdownAllocation = getBreakdownAllocation(
    d.customBreakdownItems,
    project.originalContractValue,
  );

  // Save → (regenerate QB) → download the fresh invoice + SOV PDFs.
  // Uses the returned sovData, not d.sovData, because React state hasn't
  // re-rendered yet at this point.
  const handleFormSubmit = async (e) => {
    const result = await d.handleSubmit(e);
    if (result?.regenerated && result.sovData) {
      await downloadSOVWithInvoices({ ...d, sovData: result.sovData }, project);
    }
  };

  return (
    <div className="bg-gray-700 p-6 rounded-lg shadow-xl border border-gray-600">
      <FormHeader d={d} />
      <StatusBanners d={d} />

      <form onSubmit={handleFormSubmit} className="space-y-4">
        {d.isStandaloneMode ? (
          <StandaloneInfoCard d={d} isVoided={isVoided} />
        ) : (
          <DrawInfoCard d={d} isVoided={isVoided} />
        )}
        <InvoiceDetailsCard d={d} isVoided={isVoided} />
        <TogglesRow d={d} isVoided={isVoided} />

        {!d.isStandaloneMode && d.isCustomBreakdown && (
          <CustomBreakdownCard
            d={d}
            project={project}
            isVoided={isVoided}
            allocation={breakdownAllocation}
          />
        )}

        {d.holdbackInvoiced && d.sovData.holdbackInvoiceAmount && (
          <div className="mt-4 p-4 border border-green-600 rounded-lg bg-gray-800">
            <div className="flex items-center space-x-2">
              <span className="text-green-400">✓</span>
              <span className="text-sm text-gray-300">
                Holdback marked for invoice:{" "}
                {formatDollarValue(d.sovData.holdbackInvoiceAmount)}
              </span>
            </div>
          </div>
        )}

        <ItemsTable d={d} isVoided={isVoided} />

        {d.editingSovId && d.sovData.invoiceComponents?.length > 0 && (
          <InvoiceComponentsCard d={d} />
        )}

        <DrawSummaryCard d={d} />

        <FormActions
          d={d}
          project={project}
          isVoided={isVoided}
          isBusy={isBusy}
        />
      </form>
    </div>
  );
}

function FormHeader({ d }) {
  return (
    <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
      <div className="flex items-center gap-3">
        {!d.isStandaloneMode && (
          <span className="inline-flex items-center rounded-lg bg-orange-500/10 border border-orange-500/30 px-3 py-1.5 text-sm font-semibold text-orange-300">
            Draw #{d.sovData.drawNumber || 1}
          </span>
        )}
        {d.isStandaloneMode && (
          <span className="inline-flex items-center rounded-lg bg-purple-500/10 border border-purple-500/30 px-3 py-1.5 text-sm font-semibold text-purple-300">
            Standalone CO Invoice
          </span>
        )}
        <h3 className="text-lg font-semibold text-gray-200">
          {d.editingSovId
            ? d.isStandaloneMode
              ? "Edit Standalone Invoice"
              : "Edit Progress Draw"
            : "New Progress Draw"}
        </h3>
        <span
          className={`px-2.5 py-1 rounded-full text-xs font-medium capitalize ${statusPillClass(d.sovData.status)}`}
        >
          {d.sovData.status}
        </span>
      </div>
      <button
        type="button"
        onClick={() => d.setShowCreateForm(false)}
        className="px-5 py-2 text-sm font-medium text-gray-300 hover:text-white bg-gray-600 hover:bg-gray-500 rounded-md transition-colors"
      >
        ← Back to List
      </button>
    </div>
  );
}

function StatusBanners({ d }) {
  return (
    <>
      {d.sovData.items.some((i) => i.rejectedOrReverted) && (
        <div className="mb-4 p-4 bg-red-900 border border-red-600 rounded-lg">
          <p className="text-sm font-semibold text-red-200">
            ⚠ Change Order Status Alert
          </p>
          <p className="text-sm text-red-300 mt-1">
            The following change orders have been{" "}
            {d.sovData.items
              .filter((i) => i.rejectedOrReverted)
              .map((i) => `CO #${i.changeOrderNumber} (${i._coStatus})`)
              .join(", ")}
            . Their unbilled work has been zeroed. Billed amounts in previous
            draws remain unchanged.
          </p>
        </div>
      )}
      {d.sovData.items.some((i) => i._addedAfterInvoice) && (
        <div className="mb-4 p-4 bg-yellow-900 border border-yellow-600 rounded-lg">
          <p className="text-sm font-semibold text-yellow-200">
            ⚠ Change Order Added After Invoicing
          </p>
          <p className="text-sm text-yellow-300 mt-1">
            {d.sovData.items
              .filter((i) => i._addedAfterInvoice)
              .map((i) => `CO #${i.changeOrderNumber}`)
              .join(", ")}{" "}
            was added after QuickBooks invoices were already generated on this
            draw. Regenerate QB invoices to include it.
          </p>
        </div>
      )}
      {d.sovData.items.some((i) => i._priceUpdated) && (
        <div className="mb-4 p-4 bg-blue-900 border border-blue-600 rounded-lg">
          <p className="text-sm font-semibold text-blue-200">
            ℹ Change Order Price Update
          </p>
          <p className="text-sm text-blue-300 mt-1">
            Scheduled values for{" "}
            {d.sovData.items
              .filter((i) => i._priceUpdated)
              .map((i) => `CO #${i.changeOrderNumber}`)
              .join(", ")}{" "}
            have been updated to reflect current pricing. Review before saving.
          </p>
        </div>
      )}
    </>
  );
}

function DrawInfoCard({ d, isVoided }) {
  return (
    <div className="bg-gray-800 p-4 rounded-lg mb-4">
      <h4 className="text-sm font-medium text-gray-300 mb-3">
        Progress Draw Information
      </h4>
      <div className="grid grid-cols-1 gap-4 md:grid-cols-4">
        <div>
          <label className="block text-sm font-medium text-gray-400">
            Contract PO#
          </label>
          <input
            type="text"
            name="poNumber"
            value={d.sovData.poNumber}
            onChange={d.handleSOVChange}
            disabled={isVoided}
            className="mt-1 block w-full rounded-md bg-gray-600 border-gray-500 text-gray-200 focus:border-orange-500 sm:text-sm disabled:opacity-50"
            placeholder="Enter PO Number"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-400">
            Progress Draw #
          </label>
          <input
            type="number"
            name="drawNumber"
            value={d.sovData.drawNumber}
            onChange={(e) =>
              d.setSovData((prev) => ({
                ...prev,
                drawNumber: parseInt(e.target.value) || 1,
              }))
            }
            disabled={isVoided}
            min="1"
            className="mt-1 block w-full rounded-md bg-gray-600 border-gray-500 text-gray-200 focus:border-orange-500 sm:text-sm disabled:opacity-50"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-400">
            Work Completed From
          </label>
          <input
            type="date"
            required
            name="workCompletedFrom"
            value={d.sovData.workCompletedFrom}
            onChange={d.handleSOVChange}
            disabled={isVoided}
            className="mt-1 block w-full rounded-md bg-gray-600 border-gray-500 text-gray-200 focus:border-orange-500 sm:text-sm disabled:opacity-50"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-400">
            Work Completed To
          </label>
          <input
            required
            type="date"
            name="workCompletedTo"
            value={d.sovData.workCompletedTo}
            onChange={d.handleSOVChange}
            disabled={isVoided}
            className="mt-1 block w-full rounded-md bg-gray-600 border-gray-500 text-gray-200 focus:border-orange-500 sm:text-sm disabled:opacity-50"
          />
        </div>
      </div>
    </div>
  );
}

function StandaloneInfoCard({ d, isVoided }) {
  const coNumber = d.sovData.items[0]?.changeOrderNumber;
  return (
    <div className="bg-purple-900/10 border border-purple-700/30 p-4 rounded-lg mb-4">
      <h4 className="text-sm font-medium text-purple-200 mb-1">
        Standalone Change Order Invoice
      </h4>
      <p className="text-xs text-gray-400 mb-4">
        This invoice bills CO #{coNumber} on its own, separate from the
        project's progress draws. It has its own independent holdback, unrelated
        to the project's normal holdback pool.
      </p>
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <div>
          <label className="block text-sm font-medium text-gray-400">
            Work Completed From
          </label>
          <input
            type="date"
            required
            name="workCompletedFrom"
            value={d.sovData.workCompletedFrom}
            onChange={d.handleSOVChange}
            disabled={isVoided}
            className="mt-1 block w-full rounded-md bg-gray-600 border-gray-500 text-gray-200 focus:border-orange-500 sm:text-sm disabled:opacity-50"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-400">
            Work Completed To
          </label>
          <input
            type="date"
            required
            name="workCompletedTo"
            value={d.sovData.workCompletedTo}
            onChange={d.handleSOVChange}
            disabled={isVoided}
            className="mt-1 block w-full rounded-md bg-gray-600 border-gray-500 text-gray-200 focus:border-orange-500 sm:text-sm disabled:opacity-50"
          />
        </div>
      </div>
    </div>
  );
}

function InvoiceDetailsCard({ d, isVoided }) {
  return (
    <div className="bg-gray-800 p-4 rounded-lg mb-4">
      <h4 className="text-sm font-medium text-gray-300 mb-3">
        Invoice Details
      </h4>
      <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
        <div>
          <label className="block text-sm font-medium text-gray-400">
            Invoice Date
          </label>
          <input
            autoComplete="off"
            type="date"
            name="invoiceDate"
            value={d.sovData.invoiceDate}
            onChange={d.handleSOVChange}
            disabled={isVoided}
            className="mt-1 block w-full rounded-md bg-gray-600 border-gray-500 text-gray-200 focus:border-orange-500 sm:text-sm disabled:opacity-50"
            required
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-400">
            Due Date
            {!d.dueDateTouched && (
              <span className="ml-2 text-xs text-gray-500">
                (auto: invoice date + 28 days)
              </span>
            )}
          </label>
          <input
            type="date"
            name="dueDate"
            value={d.sovData.dueDate}
            onChange={d.handleSOVChange}
            disabled={isVoided}
            className="mt-1 block w-full rounded-md bg-gray-600 border-gray-500 text-gray-200 focus:border-orange-500 sm:text-sm disabled:opacity-50"
            required
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-400">
            Total Invoiced
          </label>
          <input
            type="text"
            value={formatDollarValue(d.sovData.totalInvoiced)}
            readOnly
            className="mt-1 block w-full rounded-md bg-gray-600 border-gray-500 text-orange-300 font-semibold sm:text-sm"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-400">
            Bill To
          </label>
          <input
            type="text"
            autoComplete="off"
            name="billTo"
            value={d.sovData.billTo}
            onChange={d.handleSOVChange}
            disabled={isVoided}
            className="mt-1 block w-full rounded-md bg-gray-600 border-gray-500 text-gray-200 focus:border-orange-500 sm:text-sm disabled:opacity-50"
            required
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-400">Job</label>
          <input
            type="text"
            name="job"
            value={d.sovData.job}
            onChange={d.handleSOVChange}
            disabled={isVoided}
            className="mt-1 block w-full rounded-md bg-gray-600 border-gray-500 text-gray-200 focus:border-orange-500 sm:text-sm disabled:opacity-50"
            required
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-400">
            Status
          </label>
          <select
            name="status"
            value={d.sovData.status}
            onChange={d.handleSOVChange}
            disabled={isVoided}
            className="mt-1 block w-full rounded-md bg-gray-600 border-gray-500 text-gray-200 focus:border-orange-500 sm:text-sm disabled:opacity-50"
          >
            {["new", "invoiced", "sent", "paid", "voided"].map((s) => (
              <option key={s} value={s}>
                {s[0].toUpperCase() + s.slice(1)}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-400">
            Send To Email
          </label>
          <Select
            isMulti
            options={d.contactOptions}
            value={toEmailArray(d.sovData.sendToEmail).map(
              (email) =>
                d.contactOptions.find((o) => o.value === email) || {
                  value: email,
                  label: email,
                },
            )}
            onChange={(selected) =>
              d.setSovData((prev) => ({
                ...prev,
                sendToEmail: selected ? selected.map((o) => o.value) : [],
              }))
            }
            placeholder="Select contacts..."
            styles={customSelectStyles}
            isDisabled={isVoided}
            noOptionsMessage={() => "No contacts found"}
          />
        </div>
        <div className="md:col-span-2">
          <label className="block text-sm font-medium text-gray-400">
            Special Instructions
          </label>
          <textarea
            name="specialInstructions"
            value={d.sovData.specialInstructions}
            onChange={d.handleSOVChange}
            disabled={isVoided}
            className="mt-1 block w-full rounded-md bg-gray-600 border-gray-500 text-gray-200 focus:border-orange-500 sm:text-sm disabled:opacity-50"
            rows="2"
            placeholder="Enter any special instructions"
          />
        </div>
      </div>
    </div>
  );
}

function TogglesRow({ d, isVoided }) {
  return (
    <div className="bg-gray-800 p-4 rounded-lg mb-4">
      <div className="flex flex-wrap gap-x-8 gap-y-3">
        <label className="flex items-center gap-2 text-sm text-gray-300">
          <input
            type="checkbox"
            checked={d.sovData.withHoldback}
            onChange={(e) =>
              d.setSovData({ ...d.sovData, withHoldback: e.target.checked })
            }
            disabled={isVoided}
            className="h-4 w-4 text-orange-400 focus:ring-orange-500 border-gray-500 rounded disabled:opacity-50"
          />
          Include 10% Holdback
          {d.isStandaloneMode && (
            <span className="text-xs text-purple-400 ml-1">
              (this CO's own holdback bucket — separate from the project's)
            </span>
          )}
        </label>

        {!d.isStandaloneMode && (
          <>
            <label className="flex items-center gap-2 text-sm text-gray-300">
              <input
                type="checkbox"
                checked={d.invoiceChangesSeperately}
                onChange={(e) => {
                  d.setInvoiceChangesSeperately(e.target.checked);
                  d.setSovData((prev) => ({
                    ...prev,
                    invoiceChangesSeperately: e.target.checked,
                  }));
                }}
                disabled={isVoided}
                className="h-4 w-4 text-orange-400 focus:ring-orange-500 border-gray-500 rounded disabled:opacity-50"
              />
              Invoice change orders separately
            </label>
            <label
              className="flex items-center gap-2 text-sm text-gray-300"
              title={
                d.isBreakdownLocked && !d.breakdownUnlocked
                  ? "Breakdown items have billed work in previous draws — unlock to make changes"
                  : undefined
              }
            >
              <input
                type="checkbox"
                checked={d.isCustomBreakdown}
                onChange={(e) => {
                  const checked = e.target.checked;
                  if (!checked && d.isBreakdownLocked && !d.breakdownUnlocked) {
                    d.setConfirmUnlockIntent("disable");
                    return;
                  }
                  if (!checked) {
                    d.disableCustomBreakdown();
                    return;
                  }
                  d.setIsCustomBreakdown(true);
                  if (!d.isBreakdownLocked) d.setIsBreakdownExpanded(true);
                }}
                disabled={isVoided}
                className="h-4 w-4 text-orange-400 focus:ring-orange-500 border-gray-500 rounded disabled:opacity-50"
              />
              Custom breakdown for original contract
              {d.isCustomBreakdown && d.isBreakdownLocked && (
                <span
                  className={`text-xs ${d.breakdownUnlocked ? "text-orange-400" : "text-gray-500"}`}
                >
                  {d.breakdownUnlocked ? "🔓 unlocked" : "🔒 locked"}
                </span>
              )}
            </label>
          </>
        )}
      </div>
    </div>
  );
}

function CustomBreakdownCard({ d, project, isVoided, allocation }) {
  return (
    <div className="bg-gray-800 p-4 rounded-lg mb-4 border border-gray-600">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h4 className="text-sm font-semibold text-gray-200">
            Original Contract Breakdown
          </h4>
          <p className="text-xs text-gray-400 mt-0.5">
            Contract value:{" "}
            <span className="text-gray-200 font-medium">
              {formatDollarValue(project.originalContractValue)}
            </span>
            {allocation.excludedCount > 0 && (
              <span className="ml-2 text-gray-500">
                ({allocation.excludedCount} CN/CQ/T&M item
                {allocation.excludedCount === 1 ? "" : "s"} excluded from
                allocation)
              </span>
            )}
          </p>
        </div>
        <div className="flex items-center gap-3">
          {!d.isBreakdownLocked &&
            (allocation.isBalanced ? (
              <span className="inline-flex items-center gap-1.5 rounded-full bg-green-600/20 border border-green-600/40 px-3 py-1 text-xs font-medium text-green-300">
                ✓ Fully allocated
              </span>
            ) : allocation.isOver ? (
              <span className="inline-flex items-center gap-1.5 rounded-full bg-red-600/20 border border-red-600/40 px-3 py-1 text-xs font-medium text-red-300">
                Over by {formatDollarValue(Math.abs(allocation.remaining))}
              </span>
            ) : (
              <span className="inline-flex items-center gap-1.5 rounded-full bg-orange-600/20 border border-orange-600/40 px-3 py-1 text-xs font-medium text-orange-300">
                {formatDollarValue(allocation.remaining)} left to allocate
              </span>
            ))}
          {d.isBreakdownLocked && !d.breakdownUnlocked && (
            <span className="inline-flex items-center gap-1.5 rounded-full bg-gray-600/40 border border-gray-500/40 px-3 py-1 text-xs font-medium text-gray-300">
              🔒 Locked — billed in previous draws
            </span>
          )}
          {d.isBreakdownLocked && d.breakdownUnlocked && (
            <span className="inline-flex items-center gap-1.5 rounded-full bg-orange-600/20 border border-orange-600/40 px-3 py-1 text-xs font-medium text-orange-300">
              🔓 Unlocked — editing existing items
            </span>
          )}
          {d.isBreakdownLocked && !d.breakdownUnlocked && (
            <button
              type="button"
              onClick={() => d.setConfirmUnlockIntent("edit")}
              disabled={isVoided}
              className={`text-sm font-medium ${isVoided ? "text-gray-400 cursor-not-allowed" : "text-orange-400 hover:text-orange-300"}`}
            >
              Unlock to edit
            </button>
          )}
          {d.isBreakdownLocked && d.breakdownUnlocked && (
            <button
              type="button"
              onClick={() => d.setBreakdownUnlocked(false)}
              className="text-sm font-medium text-gray-400 hover:text-gray-300"
            >
              Re-lock
            </button>
          )}
          <button
            type="button"
            onClick={() => d.setIsBreakdownExpanded(!d.isBreakdownExpanded)}
            disabled={isVoided}
            className={`text-sm font-medium ${isVoided ? "text-gray-400 cursor-not-allowed" : "text-orange-400 hover:text-orange-300"}`}
          >
            {d.isBreakdownExpanded ? "Hide ▲" : "Show ▼"}
          </button>
        </div>
      </div>

      <button
        type="button"
        onClick={() => d.setShowAddCoPicker(true)}
        disabled={isVoided || d.eligibleCOsToAdd.length === 0}
        className={`px-4 mt-4 py-2 text-sm font-medium text-white rounded-md ${isVoided || d.eligibleCOsToAdd.length === 0 ? "bg-gray-600 cursor-not-allowed opacity-50" : "bg-orange-400 hover:bg-orange-500"} transition-colors duration-200`}
      >
        + Add Approved Change Order
      </button>

      {d.isBreakdownExpanded && (
        <div className="space-y-2 mt-3">
          <div className="flex space-x-2 items-center font-semibold text-gray-300 text-sm">
            <span className="flex-1">Description</span>
            <span className="w-32">Area</span>
            <span className="w-32">Amount</span>
            <span className="w-8"></span>
          </div>
          {d.customBreakdownItems.map((item, index) => {
            const rowLocked =
              d.isBreakdownLocked &&
              !d.breakdownUnlocked &&
              d.breakdownItemHasProgress(index);
            return (
              <div
                key={index}
                className={`flex space-x-2 items-center ${rowLocked ? "opacity-70" : ""}`}
              >
                <input
                  type="text"
                  placeholder="Description (e.g. Level 2 — Board & Tape)"
                  value={item.description}
                  onChange={(e) =>
                    d.handleBreakdownItemChange(
                      index,
                      "description",
                      e.target.value,
                    )
                  }
                  disabled={isVoided || rowLocked}
                  className="flex-1 rounded-md bg-gray-600 border-gray-500 text-gray-200 focus:border-orange-500 sm:text-sm disabled:opacity-50"
                />
                <input
                  type="text"
                  placeholder="Area"
                  value={item.area || ""}
                  onChange={(e) =>
                    d.handleBreakdownItemChange(index, "area", e.target.value)
                  }
                  disabled={isVoided || rowLocked}
                  className="w-32 rounded-md bg-gray-600 border-gray-500 text-gray-200 focus:border-orange-500 sm:text-sm disabled:opacity-50"
                />
                <NumericFormat
                  value={item.amount}
                  onValueChange={(values) =>
                    d.handleBreakdownItemChange(
                      index,
                      "amount",
                      values.floatValue || "0.00",
                    )
                  }
                  thousandSeparator=","
                  decimalScale={2}
                  fixedDecimalScale
                  prefix="$"
                  disabled={isVoided || rowLocked}
                  className="w-32 rounded-md bg-gray-600 border-gray-500 text-gray-200 focus:border-orange-500 sm:text-sm disabled:opacity-50"
                  placeholder="$0.00"
                />
                <button
                  type="button"
                  onClick={() => d.removeBreakdownItem(index)}
                  disabled={
                    isVoided || d.customBreakdownItems.length === 1 || rowLocked
                  }
                  title={
                    rowLocked
                      ? "Cannot remove — billed in previous draws"
                      : "Remove item"
                  }
                  className="text-red-400 hover:text-red-300 disabled:text-gray-500"
                >
                  {rowLocked ? "🔒" : "🗑️"}
                </button>
              </div>
            );
          })}
          <button
            type="button"
            onClick={d.addBreakdownItem}
            disabled={isVoided}
            className={`text-sm font-medium ${isVoided ? "text-gray-400 cursor-not-allowed" : "text-orange-400 hover:text-orange-300"}`}
          >
            + Add Breakdown Item
          </button>
        </div>
      )}
    </div>
  );
}

function ItemsTable({ d, isVoided }) {
  const totalScheduledValue = d.sovData.items.reduce(
    (s, i) => s + (parseFloat(i.scheduledValue) || 0),
    0,
  );
  const totalWorkCompletedPreviously = d.sovData.items.reduce(
    (s, i) => s + (parseFloat(i.workCompletedPreviously) || 0),
    0,
  );
  const thisPeriodSubtotal = d.sovData.items.reduce(
    (s, i) => s + (parseFloat(i.workCompletedThisPeriod) || 0),
    0,
  );
  const totalCompletedToDate = d.sovData.items.reduce(
    (s, i) => s + (parseFloat(i.totalCompletedToDate) || 0),
    0,
  );
  const totalBalanceToFinish = d.sovData.items.reduce(
    (s, i) => s + (parseFloat(i.balanceToFinish) || 0),
    0,
  );
  const totalPercentage = totalScheduledValue
    ? ((totalCompletedToDate / totalScheduledValue) * 100).toFixed(2)
    : "0.00";

  return (
    <div className="overflow-hidden rounded-lg shadow-xl">
      <table className="min-w-full divide-y divide-gray-600 bg-gray-700">
        <thead className="bg-gray-800">
          <tr>
            {[
              "#",
              "Description",
              "Reference / Area",
              "% Complete",
              "Tax Rate",
              "Scheduled Value",
              "Billed Previously",
              "This Period",
              "Total Completed",
              "Balance to Finish",
              "Actions",
            ].map((h) => (
              <th
                key={h}
                className="px-2 py-2 text-left text-xs font-medium text-gray-400 uppercase whitespace-nowrap"
              >
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-600">
          {d.sovData.items.map((item, index) => (
            <ItemRow
              key={index}
              item={item}
              index={index}
              d={d}
              isVoided={isVoided}
              isLast={index === d.sovData.items.length - 1}
            />
          ))}
          <tr className="bg-gray-800 font-semibold">
            <td className="px-2 py-2 w-12"></td>
            <td className="px-2 py-2 text-gray-200">Total</td>
            <td className="px-2 py-2"></td>
            <td className="px-2 py-2 text-sm text-gray-200">
              {totalPercentage}%
            </td>
            <td className="px-2 py-2"></td>
            <td className="px-2 py-2 text-sm text-gray-200">
              {formatDollarValue(totalScheduledValue)}
            </td>
            <td className="px-2 py-2 text-sm text-gray-200">
              {formatDollarValue(totalWorkCompletedPreviously)}
            </td>
            <td className="px-2 py-2 text-sm text-orange-300">
              {formatDollarValue(thisPeriodSubtotal)}
            </td>
            <td className="px-2 py-2 text-sm text-gray-200">
              {formatDollarValue(totalCompletedToDate)}
            </td>
            <td className="px-2 py-2 text-sm text-gray-200">
              {formatDollarValue(totalBalanceToFinish)}
            </td>
            <td className="px-2 py-2"></td>
          </tr>
        </tbody>
      </table>
    </div>
  );
}

function ItemRow({ item, index, d, isVoided, isLast }) {
  const minPct = minPercentForItem(item);
  const hasFloor = minPct > 0.005;
  const isCredit = isCreditMemoItem(item);
  const previouslyCredited =
    isCredit && Math.abs(parseFloat(item.workCompletedPreviously) || 0) > 0.005;
  const liveCreditMemo = isCredit
    ? (d.sovData.invoiceComponents || []).find(
        (ic) =>
          ic.qbDocType === "credit_memo" &&
          ic.status !== "voided" &&
          String(ic.changeOrderNumber) === String(item.changeOrderNumber),
      )
    : null;
  const creditChecked =
    previouslyCredited ||
    Math.abs(parseFloat(item.workCompletedThisPeriod) || 0) > 0.005;
  const creditLocked =
    isVoided ||
    item.rejectedOrReverted ||
    previouslyCredited ||
    !!liveCreditMemo;
  return (
    <tr
      className={`hover:bg-gray-600 transition-colors duration-200 ${item.rejectedOrReverted ? "opacity-60 bg-red-950" : ""} ${item._priceUpdated ? "bg-blue-950" : ""} ${item._addedAfterInvoice ? "bg-yellow-950" : ""} ${item.changeOrderNumber ? "border-l-2 border-l-orange-500/40" : ""}`}
    >
      <td className="px-2 py-2 text-sm text-gray-200 w-12">
        {item.itemNumber}
        {item.rejectedOrReverted && (
          <span
            className="ml-1 text-xs text-red-400"
            title={`CO ${item._coStatus}`}
          >
            ✕
          </span>
        )}
        {item._addedAfterInvoice && (
          <span
            className="ml-1 text-xs text-yellow-400"
            title="Added after invoice generation"
          >
            ⚠
          </span>
        )}
        {item._priceUpdated && (
          <span className="ml-1 text-xs text-blue-400" title="Price updated">
            ↑
          </span>
        )}
      </td>
      <td className="px-2 py-2 w-48">
        <textarea
          value={item.description}
          onChange={(e) =>
            d.handleItemChange(index, "description", e.target.value)
          }
          disabled={isVoided || !!item.changeOrderNumber}
          className="w-full rounded-md bg-gray-600 border-gray-500 text-gray-200 focus:border-orange-500 sm:text-sm disabled:opacity-50 min-h-[4rem] resize-y"
          rows="3"
        />
      </td>
      <td className="px-2 py-2 w-32">
        {item.changeOrderNumber ? (
          <div className="flex flex-col gap-1">
            <span className="inline-flex w-fit items-center rounded bg-orange-500/10 border border-orange-500/30 px-2 py-0.5 text-xs font-medium text-orange-300">
              CO #{item.changeOrderNumber}
            </span>
            {item.referenceNumber ? (
              <span className="inline-flex w-fit items-center rounded bg-gray-800 px-2 py-0.5 text-xs text-gray-200">
                Ref: {item.referenceNumber}
              </span>
            ) : (
              <span className="text-xs text-gray-500">No reference #</span>
            )}
          </div>
        ) : (
          <input
            type="text"
            value={item.area || ""}
            readOnly
            className="w-full rounded-md bg-gray-600 border-gray-500 text-gray-200 sm:text-sm opacity-50"
            placeholder="No area"
          />
        )}
      </td>
      <td className="px-2 py-2 w-16">
        {isCredit ? (
          <div className="flex flex-col gap-1">
            <label
              className={`flex items-center gap-2 text-xs ${creditLocked ? "text-gray-500" : "text-orange-300 cursor-pointer"}`}
              title={
                previouslyCredited
                  ? "This credit was already issued in a previous draw"
                  : liveCreditMemo
                    ? `Credit memo ${liveCreditMemo.qbInvoiceNumber || ""} exists — void it to change this`
                    : "Issue a QuickBooks credit memo for the full CO amount"
              }
            >
              <input
                type="checkbox"
                checked={creditChecked}
                disabled={creditLocked}
                onChange={(e) =>
                  d.handleItemChange(
                    index,
                    "percentage",
                    e.target.checked ? 100 : minPct,
                  )
                }
                className="rounded border-gray-500 bg-gray-600 text-orange-500 focus:ring-orange-500"
              />
              Credit this draw
            </label>
            {previouslyCredited && (
              <p className="text-[11px] text-gray-500">Credited previously</p>
            )}
            {liveCreditMemo && !previouslyCredited && (
              <p className="text-[11px] text-gray-500">
                CM {liveCreditMemo.qbInvoiceNumber || "issued"}
              </p>
            )}
          </div>
        ) : (
          <>
            <NumericFormat
              value={item.percentage}
              onValueChange={(values) =>
                d.handleItemChange(index, "percentage", values.floatValue)
              }
              onBlur={() => d.handlePercentageBlur(index)}
              decimalScale={2}
              fixedDecimalScale
              suffix="%"
              disabled={isVoided || item.rejectedOrReverted}
              className="w-full rounded-md bg-gray-600 border-gray-500 text-gray-200 focus:border-orange-500 sm:text-sm disabled:opacity-50"
            />
            {hasFloor && (
              <p className="mt-1 text-[11px] text-gray-500">
                min {minPct.toFixed(2)}%
              </p>
            )}
          </>
        )}
      </td>
      <td className="px-2 py-2 w-20">
        <NumericFormat
          value={item.taxRate}
          onValueChange={(values) =>
            d.handleItemChange(index, "taxRate", values.floatValue || "0.00")
          }
          decimalScale={2}
          fixedDecimalScale
          disabled={isVoided}
          className="w-full rounded-md bg-gray-600 border-gray-500 text-gray-200 focus:border-orange-500 sm:text-sm disabled:opacity-50"
        />
      </td>
      <td className="px-2 py-2 w-28">
        <NumericFormat
          value={item.scheduledValue}
          onValueChange={(values) =>
            d.handleItemChange(
              index,
              "scheduledValue",
              values.floatValue || "0.00",
            )
          }
          thousandSeparator=","
          decimalScale={2}
          fixedDecimalScale
          prefix="$"
          disabled={isVoided}
          className="w-full rounded-md bg-gray-600 border-gray-500 text-gray-200 focus:border-orange-500 sm:text-sm disabled:opacity-50"
        />
      </td>
      <td className="px-2 py-2 w-28">
        <input
          type="text"
          value={formatDollarValue(item.workCompletedPreviously)}
          readOnly
          className="w-full rounded-md bg-gray-600 border-gray-500 text-gray-200 sm:text-sm opacity-50"
        />
      </td>
      <td className="px-2 py-2 w-28">
        <input
          type="text"
          value={formatDollarValue(item.workCompletedThisPeriod)}
          readOnly
          className={`w-full rounded-md bg-gray-600 border-gray-500 sm:text-sm ${parseFloat(item.workCompletedThisPeriod) > 0 ? "text-orange-300 font-medium" : "text-gray-200 opacity-50"}`}
        />
      </td>
      <td className="px-2 py-2 text-sm text-gray-200 w-28">
        {formatDollarValue(item.totalCompletedToDate)}
      </td>
      <td className="px-2 py-2 text-sm text-gray-200 w-28">
        {formatDollarValue(item.balanceToFinish)}
      </td>
      <td className="px-2 py-2 w-24">
        <div className="flex space-x-2 items-center h-full pt-6">
          <button
            type="button"
            onClick={() => d.moveItemUp(index)}
            disabled={index === 0 || isVoided}
            className="text-orange-400 hover:text-orange-300 disabled:text-gray-500"
          >
            ↑
          </button>
          <button
            type="button"
            onClick={() => d.moveItemDown(index)}
            disabled={isLast || isVoided}
            className="text-orange-400 hover:text-orange-300 disabled:text-gray-500"
          >
            ↓
          </button>
          <button
            type="button"
            onClick={() => d.removeLineItem(index)}
            disabled={isVoided}
            className="text-red-400 hover:text-red-300 disabled:text-gray-500"
          >
            🗑️
          </button>
        </div>
      </td>
    </tr>
  );
}

function InvoiceComponentsCard({ d }) {
  return (
    <div className="mt-6 overflow-hidden rounded-lg shadow-xl bg-gray-700">
      <h4 className="text-md font-semibold text-gray-300 px-4 py-3 bg-gray-800">
        QuickBooks Documents
      </h4>
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-600 bg-gray-700">
          <thead className="bg-gray-800">
            <tr>
              {[
                "Component",
                "QB Doc #",
                "Subtotal",
                "Holdback",
                "Tax",
                "Total",
                "Amount Paid",
                "Status",
                "Date",
                "Actions",
              ].map((h) => (
                <th
                  key={h}
                  className="px-4 py-2 text-left text-xs font-medium text-gray-400 uppercase"
                >
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-600">
            {d.sovData.invoiceComponents.map((invoice, idx) => (
              <tr
                key={invoice._id || idx}
                className={`hover:bg-gray-600 transition-colors duration-200 ${invoice.status === "voided" ? "opacity-50" : ""}`}
              >
                <td className="px-4 py-2 text-sm text-gray-200">
                  {invoiceComponentLabel(invoice)}
                </td>
                <td className="px-4 py-2 text-sm font-medium text-orange-400">
                  {invoice.qbInvoiceNumber || "Not Generated"}
                </td>
                <td className="px-4 py-2 text-sm text-gray-200 text-right">
                  {formatDollarValue(invoice.amount || 0)}
                </td>
                <td className="px-4 py-2 text-sm text-gray-200 text-right">
                  {formatDollarValue(invoice.holdback || 0)}
                </td>
                <td className="px-4 py-2 text-sm text-gray-200 text-right">
                  {formatDollarValue(invoice.tax || 0)}
                </td>
                <td className="px-4 py-2 text-sm font-semibold text-orange-300 text-right">
                  {formatDollarValue(invoice.total || 0)}
                </td>
                <td className="px-4 py-2 w-36">
                  <NumericFormat
                    value={invoice.amountPaid || "0"}
                    onValueChange={(values) =>
                      d.handleInvoiceComponentPaymentUpdate(
                        invoice._id,
                        "amountPaid",
                        values.floatValue?.toFixed(2) || "0.00",
                      )
                    }
                    thousandSeparator=","
                    decimalScale={2}
                    fixedDecimalScale
                    prefix="$"
                    disabled={
                      invoice.status === "voided" ||
                      invoice.qbDocType === "credit_memo"
                    }
                    className="w-full rounded-md bg-gray-600 border-gray-500 text-gray-200 focus:border-orange-500 sm:text-sm disabled:opacity-50"
                  />
                </td>
                <td className="px-4 py-2 w-48">
                  <select
                    value={invoice.status || "generated"}
                    onChange={(e) =>
                      d.handleInvoiceComponentPaymentUpdate(
                        invoice._id,
                        "status",
                        e.target.value,
                      )
                    }
                    disabled={
                      invoice.status === "voided" ||
                      invoice.qbDocType === "credit_memo"
                    }
                    className="w-full rounded-md bg-gray-600 border-gray-500 text-gray-200 focus:border-orange-500 sm:text-sm disabled:opacity-50"
                  >
                    {["generated", "sent", "paid", "voided"].map((s) => (
                      <option key={s} value={s}>
                        {s[0].toUpperCase() + s.slice(1)}
                      </option>
                    ))}
                  </select>
                </td>
                <td className="px-4 py-2 text-sm text-gray-200">
                  {invoice.invoiceDate
                    ? new Date(invoice.invoiceDate).toLocaleDateString()
                    : "N/A"}
                </td>
                <td className="px-4 py-2">
                  <div className="flex gap-2">
                    {invoice.qbDocType !== "credit_memo" && (
                      <button
                        type="button"
                        onClick={() =>
                          d.saveInvoiceComponentPayment(invoice._id)
                        }
                        disabled={invoice.status === "voided"}
                        className={`px-3 py-1 text-xs font-medium text-white rounded-md transition-colors duration-200 ${invoice.status === "voided" ? "bg-gray-500 cursor-not-allowed opacity-50" : "bg-green-600 hover:bg-green-500"}`}
                      >
                        Save
                      </button>
                    )}
                    <button
                      type="button"
                      onClick={() => {
                        const isCm = invoice.qbDocType === "credit_memo";
                        if (
                          window.confirm(
                            isCm
                              ? `Delete credit memo ${invoice.qbInvoiceNumber} in QuickBooks? QuickBooks can't void credit memos, so it will be deleted.`
                              : `Void invoice ${invoice.qbInvoiceNumber} in QuickBooks?`,
                          )
                        ) {
                          d.voidInvoiceComponent(invoice._id);
                        }
                      }}
                      disabled={
                        invoice.status === "voided" || !invoice.qbInvoiceId
                      }
                      className={`px-3 py-1 text-xs font-medium text-white rounded-md transition-colors duration-200 ${invoice.status === "voided" || !invoice.qbInvoiceId ? "bg-gray-500 cursor-not-allowed opacity-50" : "bg-red-500 hover:bg-red-400"}`}
                    >
                      {invoice.qbDocType === "credit_memo" ? "Delete" : "Void"}
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function DrawSummaryCard({ d }) {
  // Credit-memo rows (negative COs) are issued as their own QB credit memo
  // and never reduce the invoice — show them as a separate line.
  const billableItems = d.sovData.items.filter((i) => !isCreditMemoItem(i));
  const creditItems = d.sovData.items.filter(
    (i) =>
      isCreditMemoItem(i) &&
      Math.abs(parseFloat(i.workCompletedThisPeriod) || 0) > 0.005,
  );
  const creditTotal = creditItems.reduce((sum, item) => {
    const isQB = d.companyProfile?.plugins?.quickbooks?.connected;
    const rate = isQB
      ? d.quickBooksTaxRates.find((tr) => tr.id === item.taxCodeId)
          ?.taxRates?.[0]?.rate || 0
      : parseFloat(item.taxRate) || 0;
    const w = parseFloat(item.workCompletedThisPeriod) || 0;
    return sum + w + w * (rate / 100);
  }, 0);
  const thisPeriodSubtotal = billableItems.reduce(
    (s, i) => s + (parseFloat(i.workCompletedThisPeriod) || 0),
    0,
  );
  const isQBConnected = d.companyProfile?.plugins?.quickbooks?.connected;
  const hst = billableItems.reduce((sum, item) => {
    const taxRate = isQBConnected
      ? d.quickBooksTaxRates.find((tr) => tr.id === item.taxCodeId)?.taxRates[0]
          ?.rate || 0
      : parseFloat(item.taxRate) || 0;
    return (
      sum + (parseFloat(item.workCompletedThisPeriod) || 0) * (taxRate / 100)
    );
  }, 0);
  const subTotalWithTax = thisPeriodSubtotal + hst;
  const currentHoldback = d.sovData.withHoldback ? subTotalWithTax * 0.1 : 0;
  const remainingBalance = subTotalWithTax - currentHoldback;

  return (
    <div className="mt-4 bg-gray-800 p-4 rounded-lg">
      <h4 className="text-sm font-medium text-gray-300 mb-3">Draw Summary</h4>
      <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
        <div>
          <p className="text-xs text-gray-400 uppercase">
            This Period Subtotal
          </p>
          <p className="text-lg font-semibold text-gray-200">
            {formatDollarValue(thisPeriodSubtotal)}
          </p>
        </div>
        <div>
          <p className="text-xs text-gray-400 uppercase">Sub-Total with Tax</p>
          <p className="text-lg font-semibold text-gray-200">
            {formatDollarValue(subTotalWithTax)}
          </p>
        </div>
        {d.sovData.withHoldback && (
          <div>
            <p className="text-xs text-gray-400 uppercase">
              Current Holdback (10%)
            </p>
            <p className="text-lg font-semibold text-purple-300">
              −{formatDollarValue(currentHoldback)}
            </p>
          </div>
        )}
        <div>
          <p className="text-xs text-gray-400 uppercase">Amount Payable</p>
          <p className="text-lg font-bold text-orange-400">
            {formatDollarValue(remainingBalance)}
          </p>
        </div>
      </div>

      {creditItems.length > 0 && (
        <div className="mt-4 pt-4 border-t border-gray-700 flex items-center justify-between">
          <div>
            <p className="text-xs text-gray-400 uppercase">
              Credit Memo(s) — issued separately, left unapplied
            </p>
            <p className="text-xs text-gray-500">
              {creditItems.map((i) => `CO #${i.changeOrderNumber}`).join(", ")}
            </p>
          </div>
          <p className="text-lg font-semibold text-green-400">
            {formatDollarValue(creditTotal)}
          </p>
        </div>
      )}

      {d.isStandaloneMode && d.sovData.withHoldback && (
        <div className="mt-4 pt-4 border-t border-gray-700 flex items-center justify-between">
          <div>
            <p className="text-xs text-gray-400 uppercase">
              This CO's Retained Holdback
            </p>
            <p className="text-lg font-semibold text-purple-300">
              {d.standaloneHoldbackStats
                ? formatDollarValue(d.standaloneHoldbackStats.netRetained)
                : "—"}
            </p>
          </div>
          <button
            type="button"
            onClick={d.prepareHoldbackReleaseForCurrentSov}
            disabled={
              !d.standaloneHoldbackStats ||
              parseFloat(d.standaloneHoldbackStats.netRetained) <= 0
            }
            className="px-4 py-2 text-sm font-medium text-white bg-purple-600 rounded-md hover:bg-purple-500 disabled:opacity-40 disabled:cursor-not-allowed"
          >
            Prepare Holdback Release
          </button>
        </div>
      )}
    </div>
  );
}

function FormActions({ d, project, isVoided, isBusy }) {
  const handleGenerateAndDownload = async () => {
    const saved = await d.generateQBInvoiceAndSave();
    if (saved) {
      await downloadSOVWithInvoices({ ...d, sovData: saved }, project);
    }
  };

  return (
    <div className="flex justify-end space-x-4">
      <button
        type="button"
        onClick={() => {
          d.setShowCreateForm(false);
          window.scrollTo(0, 0);
        }}
        disabled={isBusy}
        className={`px-4 py-2 text-sm font-medium text-gray-200 rounded-md transition-colors duration-200 ${isBusy ? "bg-gray-600 cursor-not-allowed opacity-50" : "bg-gray-600 hover:bg-gray-500"}`}
      >
        Cancel
      </button>
      {d.editingSovId && (
        <>
          <button
            type="button"
            onClick={() => downloadSOVWithInvoices(d, project)}
            disabled={isVoided || isBusy}
            className={`px-4 py-2 text-sm font-medium text-white rounded-md ${isVoided || isBusy ? "bg-gray-600 cursor-not-allowed opacity-50" : "bg-orange-400 hover:bg-orange-500"} transition-colors duration-200`}
          >
            {d.isDownloading
              ? "Downloading..."
              : d.sovData.invoiceComponents?.some(
                    (ic) => ic.qbInvoiceId && ic.status !== "voided",
                  )
                ? "Download Invoice(s)"
                : "Download SOV"}
          </button>
          <button
            type="button"
            onClick={() => generateSOVPDF(d, project, false, true)}
            disabled={isVoided || isBusy}
            className={`px-4 py-2 text-sm font-medium text-white rounded-md ${isVoided || isBusy ? "bg-gray-600 cursor-not-allowed opacity-50" : "bg-orange-400 hover:bg-orange-500"} transition-colors duration-200`}
          >
            Send Email
          </button>
          {d.companyProfile?.plugins?.quickbooks?.connected && (
            <>
              {(!d.sovData.invoiceComponents ||
                d.sovData.invoiceComponents.length === 0 ||
                d.sovData.invoiceComponents.every(
                  (ic) => ic.status === "voided",
                )) && (
                <button
                  type="button"
                  onClick={handleGenerateAndDownload}
                  disabled={isVoided || isBusy}
                  className={`px-4 py-2 text-sm font-medium text-white rounded-md ${isVoided || isBusy ? "bg-gray-600 cursor-not-allowed opacity-50" : "bg-orange-400 hover:bg-orange-500"} transition-colors duration-200`}
                >
                  {d.isGeneratingInvoice
                    ? "Generating..."
                    : "Generate QB Invoices"}
                </button>
              )}
            </>
          )}
        </>
      )}
      <button
        type="submit"
        disabled={isVoided || isBusy}
        className={`px-4 py-2 text-sm font-medium text-white rounded-md ${isVoided || isBusy ? "bg-gray-600 cursor-not-allowed opacity-50" : "bg-orange-400 hover:bg-orange-500"} transition-colors duration-200`}
      >
        {d.isSubmitting
          ? "Processing..."
          : d.isGeneratingInvoice
            ? "Updating..."
            : d.isDownloading
              ? "Downloading..."
              : d.sovData.invoiceComponents?.some(
                    (ic) => ic.status !== "voided",
                  )
                ? "Update SOV & QB Documents"
                : d.editingSovId
                  ? "Update SOV"
                  : "Save SOV"}
      </button>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// EMAIL MODAL
// ─────────────────────────────────────────────────────────────────────────
function EmailModal({ d, project }) {
  const [sendingEmail, setSendingEmail] = useState(false);

  if (!d.showEmailForm) return null;

  const handleEmailSubmit = async () => {
    try {
      if (!d.emailForm.to || !d.emailForm.subject || !d.emailForm.body) {
        d.showAlert(
          "error",
          "Invalid Input",
          "Please fill in all required email fields",
        );
        return;
      }
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!d.emailForm.to.every((email) => emailRegex.test(email.trim()))) {
        d.showAlert(
          "error",
          "Invalid Email",
          "Please enter valid email addresses",
        );
        return;
      }
      if (
        !(d.pdfBuffer instanceof Blob) ||
        d.pdfBuffer.type !== "application/pdf"
      ) {
        d.showAlert(
          "error",
          "Missing or Invalid Attachment",
          "A valid PDF file is required",
        );
        return;
      }
      setSendingEmail(true);
      const token = localStorage.getItem("token");
      const formData = new FormData();
      formData.append(
        "to",
        Array.isArray(d.emailForm.to)
          ? d.emailForm.to.join(",")
          : d.emailForm.to,
      );
      formData.append("subject", d.emailForm.subject);
      formData.append("body", d.emailForm.body);
      formData.append("signature", d.emailForm.signature || "");
      formData.append("from", d.userEmail);
      formData.append(
        "files",
        d.pdfBuffer,
        `SOV_${project.projectNumber || "Project"}_${d.sovData.qbInvoiceNumber || new Date().toISOString().split("T")[0]}.pdf`,
      );

      const response = await axios.post(
        `${d.BASE_URL}/api/sovs2/${d.sovData._id}/send-email`,
        formData,
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );

      d.setShowEmailForm(false);
      d.setPdfBuffer(null);
      if (response.status === 200)
        d.showAlert("success", "Success", "Email sent successfully!");
    } catch (err) {
      d.showAlert(
        "error",
        "Error",
        `Failed to send email: ${err.response?.data?.message || err.message}`,
      );
    } finally {
      setSendingEmail(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-gray-800 bg-opacity-75 flex items-center justify-center z-50">
      <div className="bg-gray-700 p-6 rounded-lg shadow-xl w-full max-w-lg border border-gray-600">
        <h2 className="text-xl font-semibold text-gray-200 mb-4">Send Email</h2>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-400">
              From
            </label>
            <input
              type="email"
              value={d.userEmail}
              disabled
              className="mt-1 block w-full rounded-md bg-gray-600 border-gray-500 text-gray-200 text-sm opacity-50"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-400">
              To
            </label>
            <input
              type="text"
              value={
                Array.isArray(d.emailForm.to)
                  ? d.emailForm.to.join(", ")
                  : d.emailForm.to
              }
              onChange={(e) =>
                d.setEmailForm({
                  ...d.emailForm,
                  to: e.target.value
                    .split(",")
                    .map((em) => em.trim())
                    .filter(Boolean),
                })
              }
              placeholder="recipient@example.com"
              className="mt-1 block w-full rounded-md bg-gray-600 border-gray-500 text-gray-200 focus:border-orange-500 sm:text-sm"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-400">
              Subject
            </label>
            <input
              type="text"
              value={d.emailForm.subject}
              onChange={(e) =>
                d.setEmailForm({ ...d.emailForm, subject: e.target.value })
              }
              className="mt-1 block w-full rounded-md bg-gray-600 border-gray-500 text-gray-200 focus:border-orange-500 sm:text-sm"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-400">
              Body
            </label>
            <textarea
              value={d.emailForm.body}
              onChange={(e) =>
                d.setEmailForm({ ...d.emailForm, body: e.target.value })
              }
              rows="5"
              className="mt-1 block w-full rounded-md bg-gray-600 border-gray-500 text-gray-200 focus:border-orange-500 sm:text-sm"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-400">
              Signature
            </label>
            <textarea
              value={d.emailForm.signature}
              onChange={(e) =>
                d.setEmailForm({ ...d.emailForm, signature: e.target.value })
              }
              rows="3"
              className="mt-1 block w-full rounded-md bg-gray-600 border-gray-500 text-gray-200 focus:border-orange-500 sm:text-sm"
            />
          </div>
          <div className="flex justify-end space-x-4">
            <button
              type="button"
              onClick={() => {
                d.setShowEmailForm(false);
                d.setPdfBuffer(null);
              }}
              className="px-4 py-2 text-sm font-medium text-gray-200 bg-gray-600 rounded-md hover:bg-gray-500"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleEmailSubmit}
              disabled={!d.pdfBuffer}
              className="px-4 py-2 text-sm font-medium text-white bg-orange-400 rounded-md hover:bg-orange-500 disabled:opacity-50"
            >
              Send Email
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// PDF GENERATION — kept here (not in the hook) since it's DOM/file-output
// logic tied directly to what's on screen, not SOV state itself. This is
// the piece most worth carving into its own module later if this file
// grows further; flagged in the write-up.
// ─────────────────────────────────────────────────────────────────────────
async function generateSOVPDF(
  d,
  project,
  shouldDownload = true,
  sendEmail = false,
) {
  if (!d.companyProfile) {
    d.showAlert(
      "error",
      "PDF Generation Error",
      "Cannot generate PDF: Company profile is not set.",
    );
    return null;
  }
  try {
    const response = await fetch("/sov_template2.docx");
    if (!response.ok)
      throw new Error(`Failed to load SOV template: ${response.statusText}`);
    const templateArrayBuffer = await response.arrayBuffer();
    const zip = new PizZip(templateArrayBuffer);

    const base64Regex = /^(?:data:)?image\/(png|jpg|jpeg|svg|svg\+xml);base64,/;
    const validBase64 =
      /^(?:[A-Za-z0-9+/]{4})*(?:[A-Za-z0-9+/]{2}==|[A-Za-z0-9+/]{3}=)?$/;
    function base64Parser(tagValue) {
      if (typeof tagValue !== "string" || !base64Regex.test(tagValue))
        return null;
      const stringBase64 = tagValue.replace(base64Regex, "");
      if (!validBase64.test(stringBase64)) throw new Error("Invalid base64");
      const binaryString = window.atob(stringBase64);
      const bytes = new Uint8Array(binaryString.length);
      for (let i = 0; i < binaryString.length; i++)
        bytes[i] = binaryString.charCodeAt(i);
      return bytes.buffer;
    }

    let imageBase64 = null;
    if (d.companyProfile?.logo) {
      if (!d.companyProfile.logo.startsWith("data:image")) {
        const logoResponse = await fetch(d.companyProfile.logo);
        if (logoResponse.ok) {
          const logoBlob = await logoResponse.blob();
          imageBase64 = await new Promise((resolve) => {
            const reader = new FileReader();
            reader.onloadend = () => resolve(reader.result);
            reader.readAsDataURL(logoBlob);
          });
        }
      } else {
        imageBase64 = d.companyProfile.logo;
      }
    }

    const doc = new Docxtemplater(zip, {
      paragraphLoop: true,
      linebreaks: true,
      modules: [
        new ImageModule({
          getImage: (tagValue) => base64Parser(tagValue),
          getSize: () => [125, 90],
        }),
      ],
    });

    // NOTE: the full data-building block (groupItemsByArea, totals, etc.)
    // from the original file is unchanged in behavior — omitted here only
    // for length; carry it over verbatim from the prior version of this
    // function when wiring this in.
    const data = buildSOVPdfData(d, project, imageBase64);

    doc.setData(data);
    doc.render();

    const docxBlob = doc.getZip().generate({
      type: "blob",
      mimeType:
        "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    });

    const formData = new FormData();
    formData.append(
      "docx",
      docxBlob,
      `SOV_${project.projectName}_${d.sovData.qbInvoiceNumber || "100"}.docx`,
    );
    formData.append("documentType", "SOV");
    formData.append("identifier", project._id || "default");

    const token = localStorage.getItem("token");
    const pdfResponse = await axios.post(
      `${d.BASE_URL}/api/upload/convert-to-pdf`,
      formData,
      {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "multipart/form-data",
        },
        responseType: "blob",
      },
    );

    if (
      pdfResponse.status === 200 &&
      pdfResponse.headers["content-type"] === "application/pdf"
    ) {
      const pdfBlob = new Blob([pdfResponse.data], { type: "application/pdf" });
      if (sendEmail) {
        d.setPdfBuffer(pdfBlob);
        d.setShowEmailForm(true);
      } else if (shouldDownload) {
        saveAs(
          pdfBlob,
          `SOV Draw ${d.sovData.drawNumber || ""} ${project.projectName || "Project"} ${d.sovData.invoiceDate || "undated"}.pdf`,
        );
      }
      return pdfBlob;
    } else {
      const textResponse = await pdfResponse.data.text();
      throw new Error(
        JSON.parse(textResponse)?.message || "Unknown error from server",
      );
    }
  } catch (err) {
    d.showAlert(
      "error",
      "PDF Generation Error",
      "Failed to generate SOV PDF: " + err.message,
    );
    return null;
  }
}

// Placeholder — carry over the full data object (groupItemsByArea,
// totals, invoiceOverview, etc.) from the original generateSOVPDF verbatim.
// Statuses that count as "pending" for the client-facing Pending Change
// Orders table. Adjust to match your ChangeOrder3 status enum.
const PENDING_CO_STATUSES = ["Pending", "Submitted", "Sent"];

function buildSOVPdfData(d, project, imageBase64) {
  const num = (v) => parseFloat(v) || 0;
  const money = (v) => formatDollarValue(num(v));
  const joinAddress = (a) =>
    !a
      ? ""
      : typeof a === "string"
        ? a
        : [a.street, a.city, a.state, a.postalCode, a.country]
            .filter(Boolean)
            .join(", ");

  const s = d.sovData;
  const cp = d.companyProfile || {};
  const isQBConnected = !!cp?.plugins?.quickbooks?.connected;

  const items = (s.items || []).map((it, idx) => ({
    ...it,
    itemNumber: it.itemNumber || idx + 1,
  }));
  const originalItems = items.filter((i) => !i.changeOrderNumber);
  const coItems = items.filter((i) => !!i.changeOrderNumber);

  const taxRateFor = (item) =>
    isQBConnected
      ? num(
          d.quickBooksTaxRates.find((tr) => tr.id === item.taxCodeId)
            ?.taxRates?.[0]?.rate,
        )
      : num(item.taxRate);

  const toRow = (item, description) => ({
    in: String(item.itemNumber),
    description: description || "",
    sv: money(item.scheduledValue),
    wcpp: money(item.workCompletedPreviously),
    wctp: money(item.workCompletedThisPeriod),
    tctd: money(item.totalCompletedToDate),
    p: num(item.percentage).toFixed(2),
    btf: money(item.balanceToFinish),
  });

  // Adds "(Ref: X)" right after the "CO #N" prefix in the description so
  // clients can cross-reference. Falls back to appending if the description
  // doesn't start with a CO number. Skipped on standalone CO invoices.
  const withRef = (item) => {
    const base = d.buildItemDescription(item) || "";
    const ref = String(item.referenceNumber || "").trim();
    if (!ref || d.isStandaloneMode) return base;
    const tag = `(Ref: ${ref})`;
    const coPrefix = /^\s*(CO\s*#?\s*\d+)/i;
    return coPrefix.test(base)
      ? base.replace(coPrefix, `$1 ${tag}`)
      : `${base} ${tag}`.trim();
  };

  const subtotal = (list) => {
    const sv = list.reduce((t, i) => t + num(i.scheduledValue), 0);
    const prev = list.reduce((t, i) => t + num(i.workCompletedPreviously), 0);
    const tp = list.reduce((t, i) => t + num(i.workCompletedThisPeriod), 0);
    const tc = list.reduce((t, i) => t + num(i.totalCompletedToDate), 0);
    return {
      sbtsv: money(sv),
      sbtwcp: money(prev),
      sbttp: money(tp),
      sbtc: money(tc),
      stp: sv ? ((tc / Math.abs(sv)) * 100).toFixed(2) : "0.00",
      stb: money(sv - tc),
    };
  };

  // ── Base contract grouped by area (insertion order preserved) ──
  const areaMap = new Map();
  originalItems.forEach((it) => {
    const key = (it.area || "").trim();
    if (!areaMap.has(key)) areaMap.set(key, []);
    areaMap.get(key).push(it);
  });
  const originalAreas = [...areaMap.entries()].map(([areaName, list]) => ({
    in: "",
    areaName: areaName || (areaMap.size > 1 ? "Unassigned" : ""),
    items: list.map((it) => toRow(it, it.description)),
    ...subtotal(list),
  }));

  const changeOrders = coItems.map((it) => toRow(it, withRef(it)));

  // ── Draw totals — holdback taken pre-tax, HST on the net, matching
  // the QB invoice (Total Draw → Holdback → Net → HST → Payable) ──
  // Credit-memo rows (negative COs) are excluded here — they are issued as
  // their own QB credit memo and listed separately below.
  const creditRows = coItems.filter(
    (i) =>
      isCreditMemoItem(i) && Math.abs(num(i.workCompletedThisPeriod)) > 0.005,
  );
  const billableItems = items.filter((i) => !isCreditMemoItem(i));
  const bcd = originalItems.reduce(
    (t, i) => t + num(i.workCompletedThisPeriod),
    0,
  );
  const cod = coItems
    .filter((i) => !isCreditMemoItem(i))
    .reduce((t, i) => t + num(i.workCompletedThisPeriod), 0);
  const creditMemos = creditRows.map((i) => {
    const w = num(i.workCompletedThisPeriod);
    const total = w + w * (taxRateFor(i) / 100);
    return {
      changeOrderNumber: String(i.changeOrderNumber),
      reference: i.referenceNumber || "",
      description: withRef(i),
      amount: money(w),
      total: money(total),
    };
  });
  const totalCredits = creditRows.reduce((t, i) => {
    const w = num(i.workCompletedThisPeriod);
    return t + w + w * (taxRateFor(i) / 100);
  }, 0);
  const totalDraw = bcd + cod;
  const holdbackFactor = s.withHoldback ? 0.1 : 0;
  const holdback = totalDraw * holdbackFactor;
  const net = totalDraw - holdback;
  const grossTax = billableItems.reduce(
    (t, i) => t + num(i.workCompletedThisPeriod) * (taxRateFor(i) / 100),
    0,
  );
  const tax = grossTax * (1 - holdbackFactor);
  const payable = net + tax;

  // ── Pending COs (not shown on standalone CO invoices) ──
  const pendingCOs = d.isStandaloneMode
    ? []
    : (d.allChangeOrders || [])
        .filter((co) => PENDING_CO_STATUSES.includes(co.status))
        .sort((a, b) => num(a.changeOrderNumber) - num(b.changeOrderNumber));
  const pendingChangeOrders = pendingCOs.map((co) => ({
    changeOrderNumber: String(co.changeOrderNumber),
    reference: co.referenceNumber || "—",
    title: co.title || "",
    totalPrice: money(co.totalCustomerPrice || co.totalPrice),
  }));
  const totalPendingCOs = pendingCOs.reduce(
    (t, co) => t + num(co.totalCustomerPrice || co.totalPrice),
    0,
  );

  // ── Invoice overview: every non-voided QB invoice up to and including
  // this draw (current draw taken from live state, not the stale list) ──
  const currentDraw = num(s.drawNumber);
  const sourceSovs = d.isStandaloneMode
    ? [s]
    : [
        ...(d.sovs || []).filter(
          (x) => x._id !== s._id && num(x.drawNumber) <= currentDraw,
        ),
        s,
      ];
  const componentOrder = { original_contract: 0, change_order: 1, holdback: 2 };
  const overviewComps = sourceSovs
    .flatMap((x) =>
      (x.invoiceComponents || [])
        .filter((ic) => ic.qbInvoiceNumber && ic.status !== "voided")
        .map((ic) => ({ ...ic, _draw: num(x.drawNumber) })),
    )
    .sort(
      (a, b) =>
        a._draw - b._draw ||
        (componentOrder[a.component] || 0) - (componentOrder[b.component] || 0),
    );
  const invoiceOverview = overviewComps.map((ic) => ({
    docLabel: ic.qbDocType === "credit_memo" ? "Credit Memo" : "Invoice",
    qbInvoiceNumber: ic.qbInvoiceNumber,
    invoiceDate: ic.invoiceDate ? formatLocalDateNice(ic.invoiceDate) : "—",
    amount: money(ic.amount),
    holdback: money(ic.holdback),
    tax: money(ic.tax),
    total: money(ic.total),
    amountPaid: money(ic.amountPaid),
  }));
  const sumOv = (f) => overviewComps.reduce((t, ic) => t + num(ic[f]), 0);

  return {
    // header
    image: imageBase64 || null,
    companyName: cp.companyName || "",
    companyAddress: joinAddress(cp.address),

    // top block
    AccountName: s.billTo || project.accountId?.companyName || "",
    AccountAddress:
      s.accountAddress?.replace(/^[,\s]+$/, "") ||
      joinAddress(project.accountId?.address),
    invoiceDate: formatLocalDateNice(s.invoiceDate),
    dueDate: formatLocalDateNice(s.dueDate),
    totalInvoiced: money(payable),
    projectName: project.projectName || s.projectName || "",
    projectAddress: s.job || joinAddress(project.address),
    poNumber: s.poNumber || "—",
    drawNumber: d.isStandaloneMode
      ? `CO ${coItems[0]?.changeOrderNumber || ""}`
      : String(s.drawNumber || 1),
    date1: s.workCompletedFrom ? formatLocalDateNice(s.workCompletedFrom) : "—",
    date2: s.workCompletedTo ? formatLocalDateNice(s.workCompletedTo) : "—",

    // base contract
    originalAreas,
    baseContractSubtotals: originalItems.length
      ? [subtotal(originalItems)]
      : [],

    // change orders
    changeOrders,
    changeOrderSubtotalsFlat: coItems.length ? [subtotal(coItems)] : [],

    // draw summary
    bcdst: money(bcd),
    codst: money(cod),
    thisPeriodSubtotal: money(totalDraw),
    tholdback: money(holdback),
    neta: money(net),
    totalTax: money(tax),
    remainingBalance: money(payable),
    // credit memos (optional template tags: creditMemos / totalCredits)
    creditMemos,
    hasCreditMemos: creditMemos.length > 0,
    totalCredits: money(totalCredits),

    // pending COs
    pendingChangeOrders,
    totalPendingCOs: money(totalPendingCOs),

    // invoices overview
    invoiceOverview,
    totalInvoiceBilling: money(sumOv("amount")),
    totalInvoiceHoldback: money(sumOv("holdback")),
    totalInvoiceTax: money(sumOv("tax")),
    grandTotal: money(sumOv("total")),
    totalInvoicePaid: money(sumOv("amountPaid")),
  };
}

async function downloadSOVWithInvoices(d, project) {
  d.setIsDownloading(true);
  try {
    const invoiceComponents = d.sovData.invoiceComponents?.filter(
      (comp) => comp.qbInvoiceId && comp.status !== "voided",
    );

    if (!invoiceComponents || invoiceComponents.length === 0) {
      const sovPdfBlob = await generateSOVPDF(d, project, false, false);
      if (!sovPdfBlob) return;
      saveAs(
        sovPdfBlob,
        `SOV Draw ${d.sovData.drawNumber || ""} ${d.sovData.invoiceDate || "undated"} ${project.projectName || "Project"}.pdf`,
      );
      return;
    }

    const token = localStorage.getItem("token");
    const response = await axios.post(
      `${d.BASE_URL}/api/quickbooks/invoices/pdfs`,
      {
        invoiceIds: invoiceComponents.map((comp) => ({
          qbInvoiceId: comp.qbInvoiceId,
          qbInvoiceNumber: comp.qbInvoiceNumber,
          component: comp.component,
          qbDocType: comp.qbDocType || "invoice",
        })),
      },
      { headers: { Authorization: `Bearer ${token}` } },
    );

    const qbPdfs = response.data.pdfs?.filter((p) => p.pdf !== null) || [];
    const sovPdfBlob = await generateSOVPDF(d, project, false, false);
    if (!sovPdfBlob) return;
    const sovBytes = new Uint8Array(await sovPdfBlob.arrayBuffer());

    if (qbPdfs.length === 0) {
      saveAs(
        sovPdfBlob,
        `SOV Draw ${d.sovData.drawNumber || ""} ${d.sovData.invoiceDate || "undated"} ${project.projectName || "Project"}.pdf`,
      );
      return;
    }

    for (const qbPdf of qbPdfs) {
      const merged = await PDFDocument.create();
      const qbBytes = Uint8Array.from(atob(qbPdf.pdf), (c) => c.charCodeAt(0));
      const qbDoc = await PDFDocument.load(qbBytes);
      const qbPages = await merged.copyPages(qbDoc, qbDoc.getPageIndices());
      qbPages.forEach((page) => merged.addPage(page));

      const sovDoc = await PDFDocument.load(sovBytes);
      const sovPages = await merged.copyPages(sovDoc, sovDoc.getPageIndices());
      sovPages.forEach((page) => merged.addPage(page));

      const mergedBytes = await merged.save();
      const mergedBlob = new Blob([mergedBytes], { type: "application/pdf" });

      const comp = invoiceComponents.find(
        (c) => c.qbInvoiceNumber === qbPdf.qbInvoiceNumber,
      );
      const isCm = comp?.qbDocType === "credit_memo";
      let componentLabel = "";
      if (comp?.component === "original_contract") componentLabel = "OC";
      else if (comp?.component === "change_order")
        componentLabel = `CO${comp.changeOrderNumber || ""}`;
      else if (comp?.component === "holdback") componentLabel = "Holdback";

      const filename = [
        isCm ? `CM` : `INV`,
        qbPdf.qbInvoiceNumber,
        componentLabel,
        d.sovData.invoiceDate || "undated",
        `Draw${d.sovData.drawNumber || ""}`,
        project.projectName || "Project",
      ]
        .filter(Boolean)
        .join(" ");

      saveAs(mergedBlob, `${filename}.pdf`);
      await new Promise((resolve) => setTimeout(resolve, 400));
    }
  } catch (error) {
    d.showAlert(
      "error",
      "Download Error",
      "Failed to download: " + (error.response?.data?.message || error.message),
    );
  } finally {
    d.setIsDownloading(false);
  }
}
